const POSITION_DIMENSION = {
  top: "height",
  bottom: "height",
  left: "width",
  right: "width",
}

const OPPOSITE_POSITION = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
}

const FLIP_POSITION = {
  height: "right",
  width: "bottom",
}

const EDGE_GAP = "var(--space-large)";

const BORDER_WIDTH = "4px";

// position: top || bottom || left || right
// bounds: {height, width}
// gap: int
export function positionPopover(position, bounds, element, trigger, {align, gap, flipAxisIfOverflows = true}) {
  // Jest knows no bounds (useWindowSize doesn't work)
  if (!bounds) {
    openPopover[position](element, trigger, {align, bounds: {}, gap, willOverflow: false});
    return;
  }

  const triggerRect = trigger.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  let finalPosition = position;
  const dimension = POSITION_DIMENSION[position];

  const availableSpace = (position) => {
    if (["top", "left"].includes(position)) {
      return triggerRect[position];
    }

    return bounds[dimension] - triggerRect[position];
  }

  const willOverflow = (position) => {
    return availableSpace(position)
      < (elementRect[dimension] + gapInt(gap) + gapInt(EDGE_GAP));
  }

  if (willOverflow(position)) {
    if (flipAxisIfOverflows) {
      if(willOverflow(OPPOSITE_POSITION[position])) {
        return positionPopover(FLIP_POSITION[dimension], bounds, element, trigger, { align: "center", gap, flipAxisIfOverflows: false });
      }
    }

    if (["top", "bottom"].includes(position)) {
      if (triggerRect.top > (bounds.height - triggerRect.bottom)) {
        finalPosition = "top";
      } else {
        finalPosition = "bottom";
      }
    } else if (["left", "right"].includes(position)) {
      if (triggerRect.left > bounds.width - triggerRect.right) {
        finalPosition = "left";
      } else {
        finalPosition = "right";
      }
    }
  }

  openPopover[finalPosition](element, trigger, {align, bounds, gap, willOverflow});
}

const gapInt = (gap) => {
  if (typeof gap === 'string' && gap.startsWith("var")) {
    return parseInt(window.getComputedStyle(document.body).getPropertyValue(gap.substring(4, gap.length-1)));
  } else {
    return gap;
  }
}

const gapStr = (gap) => {
  return Number.isInteger(gap) ? `${gap}px` : gap;
}

const openPopover = {
  top: (element, trigger, { align, bounds, gap, willOverflow }) => {
    const styles = element.style;
    const triggerTop = trigger.getBoundingClientRect().top;

    if (willOverflow) {
      styles.maxHeight = `calc(${triggerTop}px - ${gap} - ${EDGE_GAP} - ${BORDER_WIDTH})`;
    }

    const rect = element.getBoundingClientRect();
    styles.top = `calc(${triggerTop - rect.height}px - ${gap}`;

    alignHorizontally(element, trigger, align, bounds)
  },

  bottom: (element, trigger, { align, bounds, gap, willOverflow }) => {
    const triggerBottom = trigger.getBoundingClientRect().bottom;
    const styles = element.style;

    if (willOverflow) {
      const calculatedHeight = bounds.height - triggerBottom
      styles.maxHeight = `calc(${calculatedHeight}px - ${gap} - ${EDGE_GAP} - ${BORDER_WIDTH})`;
    }

    styles.top = `calc(${triggerBottom}px + ${gap})`;

    alignHorizontally(element, trigger, align, bounds);
  },

  left: (element, trigger, { align, bounds,  gap }) => {
    const { left } = trigger.getBoundingClientRect();

    const styles = element.style;
    const rect = element.getBoundingClientRect();
    styles.left = `calc(${left - rect.width}px - ${gapStr(gap)})`;

    alignVertically(element, trigger, align, bounds)
  },

  right: (element, trigger, { align, bounds,  gap }) => {
    const { width, left } = trigger.getBoundingClientRect();
    const styles = element.style;

    styles.left = `calc(${left + width}px + ${gapStr(gap)})`;

    alignVertically(element, trigger, align, bounds)
  },
}


function alignVertically(element, trigger, align, bounds) {
  const triggerRect = trigger.getBoundingClientRect();
  const elementHeight = element.getBoundingClientRect().height;

  const styles = element.style;

  let topValue;

  switch (align) {
    case "start":
      topValue = triggerRect.top;
      break;
    case "center":
      topValue = triggerRect.top - (elementHeight - triggerRect.height)/2;
      break;

    default:
      break;
  }

  if (elementHeight + topValue > bounds.height) {
    topValue = `calc(${bounds.height - elementHeight}px - ${EDGE_GAP})`;
  } else {
    topValue = topValue + "px";
  }

  styles.top = topValue;
}

function alignHorizontally(element, trigger, align) {
  const {left, width} = trigger.getBoundingClientRect();
  const elementWidth = element.getBoundingClientRect().width;

  const styles = element.style;

  let leftValue;

  switch (align) {
    case "start":
      leftValue = left
      break;
    case "center":
      leftValue = left + (width - elementWidth)/2
      break;

    default:
      break;
  }

  styles.left = leftValue + "px";
}

/** Create intersection observer for given root, target and callback. */
export function createIntersectionObserverFor(root, target, callback) {
  const options = {
    root,
    threshold: 0,
  };

  const observer = new IntersectionObserver(callback, options);
  observer.observe(target);
}

export function listenToScrollOnElement(element, callback) {
  element.addEventListener("scroll", callback);

  return () => {
    element.removeEventListener("scroll", callback);
  };
};
